/**
 * Business details and the policy pages.
 *
 * Kept out of content.js, which is the marketing copy: these are different in
 * kind, change for different reasons, and would bury the rest of the site's
 * text if they shared a file.
 *
 * They exist because card acquirers require them. Airwallex's onboarding
 * requirements ask for the company name, business registration number and
 * contact details; Terms & Conditions, a Refund policy and a Privacy policy;
 * clear pricing and service details; and terms naming a governing law that
 * matches the registered country.
 *
 * THIS IS DRAFTED COPY, NOT LEGAL ADVICE. It describes what Veni actually
 * does, in the plainest wording that satisfies the requirement — someone
 * qualified should read it before it carries real transactions, and the
 * privacy page in particular should be checked against the Personal Data
 * (Privacy) Ordinance.
 */

/** Identical in every language, and the details an acquirer reconciles. */
export const BUSINESS = {
  name: 'Veni Music Company',
  registration: '55606150',
  address: 'Room 510, Wayson Commercial Building, 28 Connaught Road West, Hong Kong',
  email: 'hello@veni.hk',
  phone: '+852 6212 2771',
  currency: 'HKD',
  jurisdiction: 'Hong Kong SAR',
};

/** Slugs are the same in every language; only the prefix changes. */
export const PAGES = ['terms', 'privacy', 'refunds'];

export const LEGAL = {
  en: {
    nav: { terms: 'Terms', privacy: 'Privacy', refunds: 'Refunds' },
    back: 'Back to the site',
    updated: 'Last updated',
    updatedDate: '2026-09-08',
    details: {
      title: 'Business details',
      rows: [
        ['Registered name', BUSINESS.name],
        ['Business registration number', BUSINESS.registration],
        ['Registered address', BUSINESS.address],
        ['Email', BUSINESS.email],
        ['Telephone', BUSINESS.phone],
      ],
    },
    terms: {
      title: 'Terms and conditions',
      sections: [
        ['Who we are', [
          `${BUSINESS.name} is a music services company registered in Hong Kong under business registration number ${BUSINESS.registration}, at ${BUSINESS.address}.`,
          'These terms cover engagements for our services. They apply unless a separate written agreement says otherwise, in which case that agreement takes precedence.',
        ]],
        ['What we do', [
          'We provide recording production, promotion and marketing, press relations, and data analysis for performers, ensembles, venues and labels.',
          'Every engagement is different, so services are scoped and quoted individually rather than sold from a price list.',
        ]],
        ['How an engagement works', [
          'You tell us about the project. We respond with a written proposal setting out the work, what you receive, the timetable and the fee.',
          'An engagement begins when you accept that proposal in writing. We then invoice you, and the invoice repeats the agreed scope and fee.',
        ]],
        ['Fees and payment', [
          `Fees are quoted per engagement and stated in ${BUSINESS.currency} unless the proposal names another currency. Any taxes or duties that apply are stated on the invoice.`,
          'Invoices are payable within the period stated on them, usually 30 days. Where a deposit is agreed, work begins once it is received.',
          'You can pay by card, by bank transfer, by FPS, or through AlipayHK or WeChat Pay. Card and wallet payments are handled by Airwallex; we never see or hold your card details.',
        ]],
        ['What you receive', [
          'The proposal states the deliverables, their format, and when they will be delivered. Where a date depends on something we need from you — recordings, artwork, approvals — the proposal says so.',
          'Rights in the finished work transfer to you on payment in full, except for anything we license from a third party, which the proposal identifies.',
        ]],
        ['Cancellation', [
          'Either of us may cancel an engagement in writing. What is refundable is set out in our refund policy.',
        ]],
        ['Liability', [
          'We are liable for the work we do. We are not liable for indirect or consequential loss, and our total liability for an engagement does not exceed the fees paid for it.',
          'Nothing here limits liability that cannot lawfully be limited.',
        ]],
        ['Governing law', [
          `These terms are governed by the laws of the ${BUSINESS.jurisdiction}, and the courts of the ${BUSINESS.jurisdiction} have exclusive jurisdiction over any dispute arising from them.`,
        ]],
        ['Contact', [
          `Questions about these terms: ${BUSINESS.email}, or ${BUSINESS.phone}.`,
        ]],
      ],
    },
    refunds: {
      title: 'Refund and cancellation policy',
      sections: [
        ['Before work begins', [
          'If you cancel before we have started, anything you have paid is refunded in full.',
        ]],
        ['Once work has begun', [
          'A deposit secures the engagement and the time reserved for it. Once work has begun it is not refundable.',
          'Beyond the deposit, you pay for the work completed up to the date you cancel, and the balance is refunded. We will set out what has been done and what that comes to.',
        ]],
        ['If we cancel', [
          'If we cannot complete an engagement, you pay only for work already delivered and we refund the rest, including any deposit.',
        ]],
        ['Asking for a refund', [
          `Write to ${BUSINESS.email} with the invoice number. We respond within five working days and pay approved refunds within ten.`,
          'Refunds go back by the method you paid with. A card payment returns to the same card; we cannot redirect it elsewhere.',
        ]],
        ['If something is wrong', [
          'If a deliverable is not what was agreed, tell us and we will put it right. Please raise it before asking your bank or card issuer, so we have the chance to fix it.',
        ]],
      ],
    },
    privacy: {
      title: 'Privacy policy',
      sections: [
        ['What this covers', [
          `How ${BUSINESS.name} handles personal data collected through this website and in the course of an engagement.`,
        ]],
        ['What we collect', [
          'From the enquiry form: your name, email address, the subject you choose, and what you write.',
          'From an engagement: the contact and billing details needed to work with you and to invoice you, and a record of the invoices and payments themselves.',
          'We do not collect card numbers. Card payments are handled by Airwallex on their own pages.',
        ]],
        ['Why we hold it', [
          'To answer your enquiry, to carry out work you have engaged us for, to invoice and be paid, and to keep the business records Hong Kong law requires us to keep.',
        ]],
        ['Who else sees it', [
          'Postmark, which delivers our email. Airwallex, which processes card and wallet payments. Amazon Web Services, which hosts this site.',
          'Each sees only what its job requires. We do not sell personal data, and we do not share it for anyone else’s marketing.',
        ]],
        ['How long we keep it', [
          'Enquiries that do not become engagements: two years. Records relating to an engagement, including invoices: seven years, which is the period Hong Kong tax law expects.',
        ]],
        ['Your rights', [
          'Under the Personal Data (Privacy) Ordinance you may ask what personal data we hold about you, ask us to correct it, and ask us to stop using it for a particular purpose.',
          `Write to ${BUSINESS.email}. We reply within 40 days, as the Ordinance requires.`,
        ]],
        ['Security', [
          'This site is served over HTTPS. Enquiries reach us by email rather than being stored on the website, and business records are held in systems we control, with access limited to the people who need it.',
        ]],
        ['Changes', [
          'If this policy changes, the date at the top of the page changes with it.',
        ]],
      ],
    },
  },

  'zh-Hant': {
    nav: { terms: '條款', privacy: '私隱', refunds: '退款' },
    back: '返回網站',
    updated: '最後更新',
    updatedDate: '2026-09-08',
    details: {
      title: '公司資料',
      rows: [
        ['註冊名稱', BUSINESS.name],
        ['商業登記號碼', BUSINESS.registration],
        ['註冊地址', BUSINESS.address],
        ['電郵', BUSINESS.email],
        ['電話', BUSINESS.phone],
      ],
    },
    terms: {
      title: '條款及細則',
      sections: [
        ['關於我們', [
          `${BUSINESS.name} 為在香港註冊的音樂服務公司，商業登記號碼 ${BUSINESS.registration}，地址為${BUSINESS.address}。`,
          '本條款適用於我們承接的各項服務。如另有書面協議，則以該協議為準。',
        ]],
        ['服務範圍', [
          '我們為演奏家、樂團、演出場地及唱片公司提供錄音製作、宣傳與市場推廣、媒體公關，以及數據分析。',
          '每個項目情況不同，服務按個別項目訂定範圍及報價，並非以價目表出售。',
        ]],
        ['合作流程', [
          '您向我們說明項目內容，我們以書面提交建議書，列明工作範圍、交付內容、時間表及費用。',
          '您以書面接受建議書後，項目即告展開。我們隨後發出發票，發票會重述已議定的範圍及費用。',
        ]],
        ['費用及付款', [
          `費用按項目報價，除建議書另有訂明外，均以${BUSINESS.currency}計算。如需繳付任何稅項，發票上會列明。`,
          '發票須於票面所載期限內付款，一般為30天。如已議定訂金，工作於收妥訂金後開始。',
          '您可以信用卡、銀行轉賬、轉數快（FPS），或 AlipayHK 及微信支付付款。信用卡及電子錢包付款由 Airwallex 處理，我們不會接觸或儲存您的卡片資料。',
        ]],
        ['交付內容', [
          '建議書會列明交付項目、格式及交付時間。若某項日期取決於您提供的材料——錄音、美術稿、審批——建議書會註明。',
          '成品的權利於全數付款後轉予您，但我們向第三方取得授權的部分除外，該等部分會在建議書中指明。',
        ]],
        ['取消', [
          '任何一方均可以書面取消項目。可退還的款項按我們的退款政策辦理。',
        ]],
        ['責任', [
          '我們對自身的工作負責，但不承擔間接或衍生損失；就任何項目的責任總額，以該項目已付費用為上限。',
          '本條款不限制法律上不得限制的責任。',
        ]],
        ['適用法律', [
          `本條款受${BUSINESS.jurisdiction}法律管轄，因本條款引起的任何爭議由${BUSINESS.jurisdiction}法院專屬管轄。`,
        ]],
        ['聯絡', [
          `如對本條款有疑問，請電郵 ${BUSINESS.email} 或致電 ${BUSINESS.phone}。`,
        ]],
      ],
    },
    refunds: {
      title: '退款及取消政策',
      sections: [
        ['工作開始前', [
          '若您在我們開始工作前取消，已付款項全數退還。',
        ]],
        ['工作開始後', [
          '訂金用以確認項目並保留相應時間，工作一經開始即不予退還。',
          '訂金以外的部分，您只需支付至取消當日已完成的工作，餘額退還。我們會列明已完成的工作及相應金額。',
        ]],
        ['若由我們取消', [
          '若我們無法完成項目，您只需支付已交付的部分，其餘款項連同訂金一併退還。',
        ]],
        ['申請退款', [
          `請電郵 ${BUSINESS.email} 並註明發票編號。我們於五個工作天內回覆，並於十個工作天內支付已批准的退款。`,
          '退款按原付款方式退回。以信用卡付款者退回同一張卡，我們無法改往其他途徑。',
        ]],
        ['如有問題', [
          '若交付內容與議定不符，請告知我們，我們會作出修正。請先與我們聯絡，讓我們有機會處理，然後才向銀行或發卡機構提出。',
        ]],
      ],
    },
    privacy: {
      title: '私隱政策',
      sections: [
        ['適用範圍', [
          `${BUSINESS.name} 如何處理透過本網站及在項目過程中收集的個人資料。`,
        ]],
        ['我們收集甚麼', [
          '查詢表格：您的姓名、電郵地址、所選主題，以及您填寫的內容。',
          '項目過程：與您合作及發出發票所需的聯絡及帳單資料，以及發票與付款紀錄本身。',
          '我們不會收集信用卡號碼。信用卡付款由 Airwallex 在其自身頁面處理。',
        ]],
        ['用途', [
          '回覆您的查詢、執行您委託的工作、發出發票及收款，以及保存香港法例要求保留的業務紀錄。',
        ]],
        ['誰會接觸到', [
          'Postmark 負責寄送電郵；Airwallex 處理信用卡及電子錢包付款；Amazon Web Services 提供本網站的寄存服務。',
          '各方只接觸其工作所需的資料。我們不會出售個人資料，亦不會為他人的市場推廣而分享資料。',
        ]],
        ['保存多久', [
          '未成為項目的查詢：兩年。與項目相關的紀錄（包括發票）：七年，即香港稅務法例所預期的期限。',
        ]],
        ['您的權利', [
          '根據《個人資料（私隱）條例》，您可查詢我們持有您哪些個人資料、要求更正，以及要求停止用於某特定用途。',
          `請電郵 ${BUSINESS.email}。我們會按條例要求於40日內回覆。`,
        ]],
        ['保安', [
          '本網站以 HTTPS 提供。查詢以電郵送達，不會儲存於網站；業務紀錄存放於我們自行管理的系統，僅限有需要的人員存取。',
        ]],
        ['修訂', [
          '本政策如有修訂，頁首日期會一併更新。',
        ]],
      ],
    },
  },

  'zh-Hans': {
    nav: { terms: '条款', privacy: '隐私', refunds: '退款' },
    back: '返回网站',
    updated: '最后更新',
    updatedDate: '2026-09-08',
    details: {
      title: '公司资料',
      rows: [
        ['注册名称', BUSINESS.name],
        ['商业登记号码', BUSINESS.registration],
        ['注册地址', BUSINESS.address],
        ['电邮', BUSINESS.email],
        ['电话', BUSINESS.phone],
      ],
    },
    terms: {
      title: '条款及细则',
      sections: [
        ['关于我们', [
          `${BUSINESS.name} 为在香港注册的音乐服务公司，商业登记号码 ${BUSINESS.registration}，地址为${BUSINESS.address}。`,
          '本条款适用于我们承接的各项服务。如另有书面协议，则以该协议为准。',
        ]],
        ['服务范围', [
          '我们为演奏家、乐团、演出场地及唱片公司提供录音制作、宣传与市场推广、媒体公关，以及数据分析。',
          '每个项目情况不同，服务按个别项目订定范围及报价，并非以价目表出售。',
        ]],
        ['合作流程', [
          '您向我们说明项目内容，我们以书面提交建议书，列明工作范围、交付内容、时间表及费用。',
          '您以书面接受建议书后，项目即告展开。我们随后发出发票，发票会重述已议定的范围及费用。',
        ]],
        ['费用及付款', [
          `费用按项目报价，除建议书另有订明外，均以${BUSINESS.currency}计算。如需缴付任何税项，发票上会列明。`,
          '发票须于票面所载期限内付款，一般为30天。如已议定订金，工作于收妥订金后开始。',
          '您可以信用卡、银行转账、转数快（FPS），或 AlipayHK 及微信支付付款。信用卡及电子钱包付款由 Airwallex 处理，我们不会接触或储存您的卡片资料。',
        ]],
        ['交付内容', [
          '建议书会列明交付项目、格式及交付时间。若某项日期取决于您提供的材料——录音、美术稿、审批——建议书会注明。',
          '成品的权利于全数付款后转予您，但我们向第三方取得授权的部分除外，该等部分会在建议书中指明。',
        ]],
        ['取消', [
          '任何一方均可以书面取消项目。可退还的款项按我们的退款政策办理。',
        ]],
        ['责任', [
          '我们对自身的工作负责，但不承担间接或衍生损失；就任何项目的责任总额，以该项目已付费用为上限。',
          '本条款不限制法律上不得限制的责任。',
        ]],
        ['适用法律', [
          `本条款受${BUSINESS.jurisdiction}法律管辖，因本条款引起的任何争议由${BUSINESS.jurisdiction}法院专属管辖。`,
        ]],
        ['联络', [
          `如对本条款有疑问，请电邮 ${BUSINESS.email} 或致电 ${BUSINESS.phone}。`,
        ]],
      ],
    },
    refunds: {
      title: '退款及取消政策',
      sections: [
        ['工作开始前', [
          '若您在我们开始工作前取消，已付款项全数退还。',
        ]],
        ['工作开始后', [
          '订金用以确认项目并保留相应时间，工作一经开始即不予退还。',
          '订金以外的部分，您只需支付至取消当日已完成的工作，余额退还。我们会列明已完成的工作及相应金额。',
        ]],
        ['若由我们取消', [
          '若我们无法完成项目，您只需支付已交付的部分，其余款项连同订金一并退还。',
        ]],
        ['申请退款', [
          `请电邮 ${BUSINESS.email} 并注明发票编号。我们于五个工作天内回复，并于十个工作天内支付已批准的退款。`,
          '退款按原付款方式退回。以信用卡付款者退回同一张卡，我们无法改往其他途径。',
        ]],
        ['如有问题', [
          '若交付内容与议定不符，请告知我们，我们会作出修正。请先与我们联络，让我们有机会处理，然后才向银行或发卡机构提出。',
        ]],
      ],
    },
    privacy: {
      title: '隐私政策',
      sections: [
        ['适用范围', [
          `${BUSINESS.name} 如何处理通过本网站及在项目过程中收集的个人资料。`,
        ]],
        ['我们收集什么', [
          '查询表格：您的姓名、电邮地址、所选主题，以及您填写的内容。',
          '项目过程：与您合作及发出发票所需的联络及账单资料，以及发票与付款记录本身。',
          '我们不会收集信用卡号码。信用卡付款由 Airwallex 在其自身页面处理。',
        ]],
        ['用途', [
          '回复您的查询、执行您委托的工作、发出发票及收款，以及保存香港法例要求保留的业务记录。',
        ]],
        ['谁会接触到', [
          'Postmark 负责寄送电邮；Airwallex 处理信用卡及电子钱包付款；Amazon Web Services 提供本网站的寄存服务。',
          '各方只接触其工作所需的资料。我们不会出售个人资料，亦不会为他人的市场推广而分享资料。',
        ]],
        ['保存多久', [
          '未成为项目的查询：两年。与项目相关的记录（包括发票）：七年，即香港税务法例所预期的期限。',
        ]],
        ['您的权利', [
          '根据《个人资料（隐私）条例》，您可查询我们持有您哪些个人资料、要求更正，以及要求停止用于某特定用途。',
          `请电邮 ${BUSINESS.email}。我们会按条例要求于40日内回复。`,
        ]],
        ['安全', [
          '本网站以 HTTPS 提供。查询以电邮送达，不会储存于网站；业务记录存放于我们自行管理的系统，仅限有需要的人员存取。',
        ]],
        ['修订', [
          '本政策如有修订，页首日期会一并更新。',
        ]],
      ],
    },
  },
  ja: {
    nav: { terms: '規約', privacy: 'プライバシー', refunds: '返金' },
    back: 'サイトへ戻る',
    updated: '最終更新',
    updatedDate: '2026-09-08',
    details: {
      title: '事業者情報',
      rows: [
        ['登記名称', BUSINESS.name],
        ['事業登録番号', BUSINESS.registration],
        ['登記住所', BUSINESS.address],
        ['メール', BUSINESS.email],
        ['電話', BUSINESS.phone],
      ],
    },
    terms: {
      title: '利用規約',
      sections: [
        ['当社について', [
          `${BUSINESS.name} は香港で登記された音楽サービス会社です。事業登録番号 ${BUSINESS.registration}、所在地 ${BUSINESS.address}。`,
          '本規約は当社が受託する業務に適用されます。別途の書面による合意がある場合は、その合意が優先します。',
        ]],
        ['業務内容', [
          '演奏家、アンサンブル、会場、レーベルに向けて、録音制作、プロモーションとマーケティング、メディア対応、データ分析を提供します。',
          '案件ごとに内容が異なるため、価格表による販売ではなく、個別に範囲を定めてお見積りします。',
        ]],
        ['ご依頼の流れ', [
          'ご相談の内容を伺い、作業範囲、納品物、日程、料金を記載した提案書を書面でお送りします。',
          'ご承諾をいただいた時点で業務を開始します。その後、合意した範囲と料金を記載した請求書を発行します。',
        ]],
        ['料金と支払い', [
          `料金は案件ごとのお見積りで、提案書に別段の記載がない限り${BUSINESS.currency}建てです。税金が発生する場合は請求書に記載します。`,
          '請求書は記載の期限（通常30日）までにお支払いください。着手金の合意がある場合、入金確認後に作業を開始します。',
          'カード、銀行振込、FPS、AlipayHK、WeChat Pay でお支払いいただけます。カードおよびウォレット決済は Airwallex が処理し、当社がカード情報を取得・保管することはありません。',
        ]],
        ['納品物', [
          '納品物、形式、納品時期は提案書に記載します。録音、素材、承認など、お客様からご提供いただくものに日程が依存する場合は、その旨を明記します。',
          '完成物の権利は全額のお支払いをもってお客様に移転します。ただし第三者からライセンスを受けた部分は除き、提案書に明示します。',
        ]],
        ['キャンセル', [
          'いずれの当事者も書面によりキャンセルできます。返金の取扱いは返金ポリシーによります。',
        ]],
        ['責任', [
          '当社は自らの業務について責任を負いますが、間接損害・結果的損害については責任を負いません。案件に関する責任の総額は、当該案件についてお支払いいただいた金額を上限とします。',
          '法律上制限できない責任を制限するものではありません。',
        ]],
        ['準拠法', [
          `本規約は${BUSINESS.jurisdiction}の法律に準拠し、本規約に起因する紛争については${BUSINESS.jurisdiction}の裁判所が専属的管轄権を有します。`,
        ]],
        ['お問い合わせ', [
          `本規約についてのお問い合わせは ${BUSINESS.email} または ${BUSINESS.phone} まで。`,
        ]],
      ],
    },
    refunds: {
      title: '返金・キャンセルポリシー',
      sections: [
        ['作業開始前', [
          '作業開始前にキャンセルされた場合、お支払いいただいた金額は全額返金します。',
        ]],
        ['作業開始後', [
          '着手金は案件と確保した時間に対するもので、作業開始後は返金いたしません。',
          '着手金を超える部分については、キャンセル日までに完了した作業分のみをご負担いただき、残額を返金します。完了した作業とその金額は明細でお示しします。',
        ]],
        ['当社からのキャンセル', [
          '当社が案件を完了できない場合、納品済みの分のみをご負担いただき、着手金を含む残額を返金します。',
        ]],
        ['返金のご依頼', [
          `請求書番号を添えて ${BUSINESS.email} までご連絡ください。5営業日以内に回答し、承認された返金は10営業日以内にお支払いします。`,
          '返金はお支払いいただいた方法へお戻しします。カード決済の場合は同一のカードへの返金となり、他の方法へ振り替えることはできません。',
        ]],
        ['不備があった場合', [
          '納品物が合意した内容と異なる場合は、お知らせいただければ是正します。銀行やカード発行会社へのお申し出の前に、まず当社にご連絡ください。',
        ]],
      ],
    },
    privacy: {
      title: 'プライバシーポリシー',
      sections: [
        ['対象', [
          `${BUSINESS.name} が本ウェブサイトおよび業務の過程で取得する個人情報の取扱いについて説明します。`,
        ]],
        ['取得する情報', [
          'お問い合わせフォーム：お名前、メールアドレス、選択された件名、ご記入いただいた内容。',
          '業務の過程：お取引と請求に必要な連絡先および請求先情報、ならびに請求と支払いの記録。',
          'カード番号は取得しません。カード決済は Airwallex が自社のページで処理します。',
        ]],
        ['利用目的', [
          'お問い合わせへの回答、ご依頼いただいた業務の遂行、請求と入金の管理、および香港法が求める事業記録の保存のため。',
        ]],
        ['第三者への提供', [
          'メール配信は Postmark、カードおよびウォレット決済は Airwallex、本サイトのホスティングは Amazon Web Services が担っています。',
          '各社はその業務に必要な範囲のみを取り扱います。個人情報を販売することはなく、第三者のマーケティングのために共有することもありません。',
        ]],
        ['保存期間', [
          '案件に至らなかったお問い合わせは2年、請求書を含む案件に関する記録は7年（香港の税務上想定される期間）保存します。',
        ]],
        ['お客様の権利', [
          '個人データ（プライバシー）条例に基づき、当社が保有する個人情報の開示、訂正、および特定の目的での利用停止を求めることができます。',
          `${BUSINESS.email} までご連絡ください。同条例の定めに従い40日以内に回答します。`,
        ]],
        ['安全管理', [
          '本サイトは HTTPS で提供しています。お問い合わせはサイトに保存されずメールで届き、業務記録は当社が管理するシステムに、必要な者のみがアクセスできる形で保管しています。',
        ]],
        ['変更', [
          '本ポリシーを変更した場合は、ページ上部の日付も更新します。',
        ]],
      ],
    },
  },
};
